import { useState } from 'react';

const clamp = (n) => Math.min(Math.max(n, 0), 1);

// '#RRGGBB' and { h: 0–360, s: 0–1, v: 0–1 }.
export const hexToHsv = (hex) => {
    const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
    const max = Math.max(r, g, b);
    const d = max - Math.min(r, g, b);
    let h = 0;
    if (d > 0) {
        if (max === r) h = ((g - b) / d) % 6;
        else if (max === g) h = (b - r) / d + 2;
        else h = (r - g) / d + 4;
    }
    return { h: (h * 60 + 360) % 360, s: max === 0 ? 0 : d / max, v: max };
};

export const hsvToHex = ({ h, s, v }) => {
    const channel = (n) => {
        const k = (n + h / 60) % 6;
        return v - v * s * Math.max(Math.min(k, 4 - k, 1), 0);
    };
    return `#${[5, 3, 1].map((n) => Math.round(channel(n) * 255).toString(16).padStart(2, '0')).join('')}`.toUpperCase();
};

// A colour of one's own, picked in the palette itself: a square for the shade, a bar for the hue, or the hex
// code typed in. Nothing is taken until Добавить is pressed. initial is where it starts, '#RRGGBB'.
function CustomColor({ initial, onAdd, onCancel }) {
    const [hsv, setHsv] = useState(() => hexToHsv(initial ?? '#069685'));
    const hex = hsvToHex(hsv);
    // A code being typed, shown as typed until the field is left.
    const [typed, setTyped] = useState(null);

    // Follows the pointer from a press in the square or on the bar until it's let go, even outside them.
    const drag = (update) => ({
        onPointerDown: (e) => {
            e.preventDefault();
            e.currentTarget.setPointerCapture(e.pointerId);
            update(e);
        },
        onPointerMove: (e) => {
            if (e.currentTarget.hasPointerCapture(e.pointerId)) update(e);
        },
    });
    const at = (e) => {
        const box = e.currentTarget.getBoundingClientRect();
        return { x: clamp((e.clientX - box.left) / box.width), y: clamp((e.clientY - box.top) / box.height) };
    };
    const fromSquare = (e) => {
        const { x, y } = at(e);
        setHsv((current) => ({ ...current, s: x, v: 1 - y }));
    };
    const fromBar = (e) => {
        const { x } = at(e);
        setHsv((current) => ({ ...current, h: x * 360 }));
    };

    const onType = (e) => {
        const text = e.target.value.trim();
        setTyped(text);
        const match = /^#?([0-9a-f]{6})$/i.exec(text);
        if (match) setHsv(hexToHsv(`#${match[1]}`));
    };

    // Adding or cancelling closes the editor or the palette under the pointer, so the press stops here rather
    // than reaching the day window as one outside it.
    const press = (action) => (e) => {
        e.preventDefault();
        e.stopPropagation();
        action();
    };

    return (
        <div className="custom-color">
            <div
                className="custom-color-square"
                style={{ background: `linear-gradient(to top, #000, rgba(0, 0, 0, 0)), linear-gradient(to right, #fff, rgba(255, 255, 255, 0)), hsl(${hsv.h}, 100%, 50%)` }}
                {...drag(fromSquare)}
            >
                <i className="custom-color-thumb" style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%`, background: hex }} />
            </div>
            <div className="custom-color-hue" {...drag(fromBar)}>
                <i className="custom-color-thumb" style={{ left: `${(hsv.h / 360) * 100}%`, background: `hsl(${hsv.h}, 100%, 50%)` }} />
            </div>
            <div className="custom-color-row">
                <i className="custom-color-preview" style={{ background: hex }} />
                <input
                    className="custom-color-hex"
                    aria-label="Код цвета"
                    value={typed ?? hex}
                    maxLength={7}
                    onChange={onType}
                    onBlur={() => setTyped(null)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onAdd(hex); } }}
                />
                <button type="button" className="custom-color-cancel" onMouseDown={press(onCancel)}>Отмена</button>
                <button type="button" className="btn btn-green custom-color-add" onMouseDown={press(() => onAdd(hex))}>Добавить</button>
            </div>
        </div>
    );
}

export { CustomColor };
